import { useDeletePackageMutation, useDeleteLayoutOptimizationMutation } from '@consigli/hooks';
import { ServiceEntityType } from '@consigli/types';
import { useCallback, useState } from 'react';

import { errorToast, successToast } from '@/components/toast';
import { getDeleteParams } from '@/utils/type-guards';
import { Service } from '@/utils/types';

interface ServiceToDelete {
  service: Service;
  projectId: number;
}

/**
 * Hook for deleting a service. Manages the fact that different service types have different deletion logic.
 * @returns The hook for deleting a service.
 */
export const useDeleteService = () => {
  const [deletePackage] = useDeletePackageMutation();
  const [deleteLayoutOptimization] = useDeleteLayoutOptimizationMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<ServiceToDelete | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = useCallback((service: Service, projectId: number) => {
    setServiceToDelete({ service, projectId });
    setIsModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!serviceToDelete) return;

    setIsDeleting(true);
    try {
      const deleteParams = getDeleteParams(serviceToDelete.service, serviceToDelete.projectId);

      //TODO: Add building package deletion
      if (deleteParams.type === ServiceEntityType.DOCUMENT_PACKAGE) {
        await deletePackage(deleteParams.params).unwrap();
        successToast({ message: 'Package deleted successfully' });
      } else if (deleteParams.type === ServiceEntityType.LAYOUT_OPTIMIZATION) {
        await deleteLayoutOptimization(deleteParams.params).unwrap();
        successToast({ message: 'Layout optimization deleted successfully' });
      }

      setIsModalOpen(false);
      setServiceToDelete(null);
      // The table will automatically refresh due to the query invalidation
    } catch (error) {
      console.error('Failed to delete service:', error);
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Unknown error';
      errorToast({ message: 'Failed to delete service: ' + message });
    } finally {
      setIsDeleting(false);
    }
  }, [deletePackage, deleteLayoutOptimization, serviceToDelete]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setServiceToDelete(null);
  }, []);

  return {
    isModalOpen,
    serviceToDelete,
    isDeleting,
    handleDeleteClick,
    handleConfirmDelete,
    handleCloseModal,
  };
};
